/**
 * MintVerifySandbox — 60-second interactive panel on /kypa/instrument.
 *
 * Flow:
 *   1. Fetch demo agents from /api/kypa/credential/demo-agents (server-signed).
 *   2. User picks scope (max_amount_cents, allowed_domains, purpose, ttl).
 *   3. Mint → shows decoded JSON envelope + wire header.
 *   4. Verify → shows nine-check breakdown with pass/fail.
 *   5. "Break it" preset buttons demonstrate exactly which check fails.
 *
 * No keys ever touch the browser. The backend uses KYPA_DEMO_SIGNING_KEY
 * for `is_demo: true` profiles only.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle2, XCircle, Copy, Play, ShieldOff, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <button
      onClick={doCopy}
      className="text-[10px] text-slate-500 hover:text-white inline-flex items-center gap-1 font-mono uppercase tracking-wider"
      data-testid="sandbox-copy-btn"
    >
      <Copy className="w-3 h-3" />
      {copied ? 'copied' : 'copy'}
    </button>
  );
}

export default function MintVerifySandbox() {
  const [demoAgents, setDemoAgents] = useState(null);
  const [demoErr, setDemoErr] = useState(null);
  const [selectedKypaId, setSelectedKypaId] = useState('');

  // Scope inputs
  const [maxAmountCents, setMaxAmountCents] = useState(5000);
  const [allowedDomain, setAllowedDomain] = useState('*.merchant.com');
  const [purpose, setPurpose] = useState('checkout');
  const [ttlSeconds, setTtlSeconds] = useState(300);

  // Mint result
  const [minting, setMinting] = useState(false);
  const [mintResult, setMintResult] = useState(null);
  const [mintErr, setMintErr] = useState(null);

  // Verify inputs
  const [requestedAmountCents, setRequestedAmountCents] = useState(500);
  const [targetDomain, setTargetDomain] = useState('shop.merchant.com');

  // Verify result
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyErr, setVerifyErr] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/kypa/credential/demo-agents`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        setDemoAgents(d.agents || []);
        if (d.agents?.[0]?.kypa_id) setSelectedKypaId(d.agents[0].kypa_id);
      })
      .catch((e) => setDemoErr(e.message));
  }, []);

  const selectedAgent = useMemo(
    () => (demoAgents || []).find((a) => a.kypa_id === selectedKypaId),
    [demoAgents, selectedKypaId],
  );

  const doMint = async () => {
    setMinting(true);
    setMintErr(null);
    setMintResult(null);
    setVerifyResult(null);
    try {
      const r = await fetch(`${API}/api/kypa/credential/mint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kypa_id: selectedKypaId,
          max_amount_cents: Number(maxAmountCents),
          allowed_domains: allowedDomain ? [allowedDomain] : [],
          purpose: purpose || 'checkout',
          ttl_seconds: Number(ttlSeconds),
        }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
      setMintResult(body);
    } catch (e) {
      setMintErr(String(e.message || e));
    }
    setMinting(false);
  };

  const doVerify = async (overrides = {}) => {
    if (!mintResult?.wire_header) return;
    setVerifying(true);
    setVerifyErr(null);
    setVerifyResult(null);
    const payload = {
      wire: overrides.wire ?? mintResult.wire_header,
      requested_amount_cents: overrides.requested_amount_cents ?? Number(requestedAmountCents),
      target_domain: overrides.target_domain ?? targetDomain,
    };
    try {
      const r = await fetch(`${API}/api/kypa/credential/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
      setVerifyResult({ ...body, _preset: overrides._preset || 'happy' });
    } catch (e) {
      setVerifyErr(String(e.message || e));
    }
    setVerifying(false);
  };

  const doVerifyOverAmount = () =>
    doVerify({ requested_amount_cents: Number(maxAmountCents) + 1, _preset: 'over_amount' });
  const doVerifyWrongDomain = () =>
    doVerify({ target_domain: 'evil.example.com', _preset: 'wrong_domain' });
  const doVerifyTampered = () => {
    if (!mintResult?.wire_header) return;
    const [body, sig] = mintResult.wire_header.split('.');
    const tampered = body.slice(0, -2) + 'AA.' + sig;
    doVerify({ wire: tampered, _preset: 'tampered' });
  };

  if (demoErr) {
    return (
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.06] p-6 text-sm text-rose-200" data-testid="sandbox-error">
        Sandbox unavailable ({demoErr}). Please try again in a moment.
      </div>
    );
  }
  if (demoAgents === null) {
    return (
      <div className="text-slate-500 text-sm inline-flex items-center gap-2" data-testid="sandbox-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading sandbox…
      </div>
    );
  }
  if (demoAgents.length === 0) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6 text-sm text-slate-400" data-testid="sandbox-empty">
        No demo agents seeded yet. Ask an admin to seed one via the KYPA sandbox script.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mint-verify-sandbox">
      {/* Step 1: choose demo agent + scope */}
      <div className="bg-[#0f0f16] border border-white/10 rounded-xl p-5">
        <div className="text-[10px] font-bold tracking-[1.5px] text-emerald-300 mb-3">STEP 1 · SET THE SCOPE</div>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <label className="block">
            <span className="text-xs text-slate-400 mb-1 block">Demo agent</span>
            <select
              value={selectedKypaId}
              onChange={(e) => setSelectedKypaId(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-emerald-500/50 focus:outline-none"
              data-testid="sandbox-agent-select"
            >
              {demoAgents.map((a) => (
                <option key={a.kypa_id} value={a.kypa_id}>
                  {a.agent_name} — {a.pledge_tier} — ${a.pledge_amount_usdc?.toLocaleString()}
                </option>
              ))}
            </select>
            {selectedAgent && (
              <div className="text-[10px] text-slate-500 mt-1 font-mono truncate">
                wallet: {selectedAgent.wallet_address}
              </div>
            )}
          </label>
          <label className="block">
            <span className="text-xs text-slate-400 mb-1 block">Purpose</span>
            <input
              type="text"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              maxLength={64}
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-emerald-500/50 focus:outline-none"
              data-testid="sandbox-purpose"
            />
          </label>
          <label className="block">
            <span className="text-xs text-slate-400 mb-1 block">Max amount (US cents)</span>
            <input
              type="number"
              value={maxAmountCents}
              onChange={(e) => setMaxAmountCents(e.target.value)}
              min="1"
              max="100000000"
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-emerald-500/50 focus:outline-none"
              data-testid="sandbox-max-amount"
            />
            <div className="text-[10px] text-slate-500 mt-1">
              ≈ ${(Number(maxAmountCents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </label>
          <label className="block">
            <span className="text-xs text-slate-400 mb-1 block">Allowed domain (wildcard OK)</span>
            <input
              type="text"
              value={allowedDomain}
              onChange={(e) => setAllowedDomain(e.target.value)}
              placeholder="*.merchant.com"
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-emerald-500/50 focus:outline-none"
              data-testid="sandbox-allowed-domain"
            />
          </label>
          <label className="block">
            <span className="text-xs text-slate-400 mb-1 block">TTL (seconds)</span>
            <input
              type="number"
              value={ttlSeconds}
              onChange={(e) => setTtlSeconds(e.target.value)}
              min="30"
              max="3600"
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-emerald-500/50 focus:outline-none"
              data-testid="sandbox-ttl"
            />
          </label>
        </div>
        <button
          onClick={doMint}
          disabled={minting || !selectedKypaId}
          className="w-full h-11 rounded-lg bg-emerald-500 text-black font-semibold text-sm hover:brightness-110 disabled:opacity-50 inline-flex items-center justify-center gap-2 transition"
          data-testid="sandbox-mint-btn"
        >
          {minting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {minting ? 'Minting…' : 'Mint Credential'}
        </button>
        {mintErr && (
          <div className="mt-3 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded px-3 py-2" data-testid="sandbox-mint-error">
            {mintErr}
          </div>
        )}
      </div>

      {/* Step 2: minted output */}
      {mintResult && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#0f0f16] border border-emerald-500/30 rounded-xl p-5"
          data-testid="sandbox-mint-result"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-bold tracking-[1.5px] text-emerald-300">STEP 2 · CREDENTIAL MINTED</div>
            <div className="text-[10px] text-slate-500">
              signed by server demo key
            </div>
          </div>

          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">X-KYPA-Credential (wire header)</div>
              <CopyBtn text={mintResult.wire_header} />
            </div>
            <div className="bg-black/60 border border-white/10 rounded-md p-3 text-[11px] font-mono text-slate-300 break-all leading-relaxed max-h-24 overflow-y-auto" data-testid="sandbox-wire">
              {mintResult.wire_header}
            </div>
          </div>

          <details className="mt-3">
            <summary className="text-xs text-slate-400 cursor-pointer hover:text-white">Decoded envelope JSON</summary>
            <pre className="mt-2 bg-black/60 border border-white/10 rounded-md p-3 text-[11px] font-mono text-slate-300 overflow-x-auto leading-relaxed">
              {JSON.stringify(mintResult.envelope, null, 2)}
            </pre>
          </details>
        </motion.div>
      )}

      {/* Step 3: verify */}
      {mintResult && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#0f0f16] border border-white/10 rounded-xl p-5"
          data-testid="sandbox-verify-panel"
        >
          <div className="text-[10px] font-bold tracking-[1.5px] text-cyan-300 mb-3">STEP 3 · VERIFY (WHAT A MERCHANT WOULD DO)</div>
          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <label className="block">
              <span className="text-xs text-slate-400 mb-1 block">Requested amount (US cents)</span>
              <input
                type="number"
                value={requestedAmountCents}
                onChange={(e) => setRequestedAmountCents(e.target.value)}
                min="0"
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-500/50 focus:outline-none"
                data-testid="sandbox-req-amount"
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-400 mb-1 block">Target domain (merchant hostname)</span>
              <input
                type="text"
                value={targetDomain}
                onChange={(e) => setTargetDomain(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-500/50 focus:outline-none"
                data-testid="sandbox-req-domain"
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            <button
              onClick={() => doVerify()}
              disabled={verifying}
              className="px-4 h-10 rounded-md bg-cyan-500 text-black font-semibold text-sm hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="sandbox-verify-btn"
            >
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Verify
            </button>
            <div className="inline-flex items-center gap-1 text-[10px] text-slate-500 uppercase tracking-wider pl-2 pr-1">
              or break it:
            </div>
            <button
              onClick={doVerifyOverAmount}
              disabled={verifying}
              className="px-3 h-10 rounded-md border border-white/15 text-slate-300 text-xs hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="sandbox-break-amount"
            >
              <ShieldOff className="w-3.5 h-3.5" /> over amount
            </button>
            <button
              onClick={doVerifyWrongDomain}
              disabled={verifying}
              className="px-3 h-10 rounded-md border border-white/15 text-slate-300 text-xs hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="sandbox-break-domain"
            >
              <ShieldOff className="w-3.5 h-3.5" /> wrong domain
            </button>
            <button
              onClick={doVerifyTampered}
              disabled={verifying}
              className="px-3 h-10 rounded-md border border-white/15 text-slate-300 text-xs hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="sandbox-break-tampered"
            >
              <ShieldOff className="w-3.5 h-3.5" /> tampered wire
            </button>
          </div>

          {verifyErr && (
            <div className="mt-3 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded px-3 py-2" data-testid="sandbox-verify-error">
              {verifyErr}
            </div>
          )}

          {verifyResult && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-4 rounded-lg border p-4 ${verifyResult.valid ? 'border-emerald-500/40 bg-emerald-500/[0.05]' : 'border-rose-500/40 bg-rose-500/[0.06]'}`}
              data-testid="sandbox-verify-result"
            >
              <div className="flex items-baseline justify-between mb-3">
                <div className={`text-lg font-black ${verifyResult.valid ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {verifyResult.valid ? 'VALID → accept request' : 'INVALID → merchant returns 402'}
                </div>
                {verifyResult._preset !== 'happy' && (
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    preset: {verifyResult._preset}
                  </div>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {verifyResult.checks?.map((c) => (
                  <div key={c.check} className="flex items-start gap-2 py-1">
                    {c.passed ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-rose-400 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0">
                      <div className="font-mono text-slate-200">{c.check}</div>
                      {c.reason && (
                        <div className="text-[10px] text-rose-300/80 break-all">{c.reason}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {verifyResult.agent && (
                <div className="mt-3 pt-3 border-t border-white/10 text-[11px] text-slate-400 leading-relaxed">
                  Agent identified: <span className="text-white font-medium">{verifyResult.agent.agent_name}</span> · pledge <span className="text-emerald-300">${verifyResult.agent.pledge_amount_usdc?.toLocaleString()}</span> · framework {verifyResult.agent.framework}
                </div>
              )}
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Reset */}
      {(mintResult || mintErr) && (
        <button
          onClick={() => { setMintResult(null); setMintErr(null); setVerifyResult(null); setVerifyErr(null); }}
          className="text-xs text-slate-500 hover:text-white inline-flex items-center gap-1"
          data-testid="sandbox-reset"
        >
          <RefreshCw className="w-3 h-3" /> Reset sandbox
        </button>
      )}
    </div>
  );
}
